import type { SvgIconComponent } from '@mui/icons-material';
import CancelOutlined from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import SendOutlined from '@mui/icons-material/Send';
import { Card } from '@ovComponents/2-component/card/card';
import { DashboardCard } from '@ovComponents/2-component/dashboardCard/dashboardCard';
import { StatusTag } from '@ovComponents/2-component/statusTag/statusTag';
import {
  Table,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { Alert, Box, Skeleton, Typography, useTheme } from 'hephaestus/web';
import type { TFunction } from 'i18next';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  BatchRecapEvent,
  BatchRecapEventType,
  BatchRecapFilter,
  BatchRecapFilterCounts,
  BatchRecapFlag,
  BatchRecapProps,
  BatchRecapStatus,
  BatchRecapSummary,
  BatchRecapSummaryCard,
} from './batchRecap.types';

type StatusTagProps = {
  color: 'positive' | 'primary' | 'negative';
  icon: SvgIconComponent;
  label: string;
};

function statusToTagProps(
  status: BatchRecapStatus,
  t: TFunction,
): StatusTagProps {
  switch (status) {
    case 'COMPLETED':
      return {
        color: 'positive',
        icon: CheckCircle,
        label: t('status.COMPLETED'),
      };
    case 'SENT':
      return {
        color: 'primary',
        icon: SendOutlined,
        label: t('status.SENT'),
      };
    case 'FAILED':
      return {
        color: 'negative',
        icon: ErrorOutline,
        label: t('status.FAILED'),
      };
    case 'CANCELED':
      return {
        color: 'negative',
        icon: CancelOutlined,
        label: t('status.CANCELED'),
      };
  }
}

const ORDERED_FILTERS: readonly BatchRecapFilter[] = [
  'ALL',
  'COMPLETED',
  'TRADES_SENT',
  'FAILED_CANCELED',
] as const;

function isSummaryEmpty(summary: BatchRecapSummary): boolean {
  return (
    summary.batchesRun.value === 0 &&
    summary.tradesSent.value === 0 &&
    summary.portfoliosRebalanced.value === 0 &&
    summary.failedOrCanceled.value === 0
  );
}

const EventTypePill: FC<{ eventType: BatchRecapEventType }> = ({
  eventType,
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        px: 1,
        py: 0.25,
        borderRadius: 8,
        backgroundColor: theme.color.surfaceContainer,
        color: theme.color.onSurfaceVariant,
        display: 'inline-block',
        minWidth: 56,
        textAlign: 'center',
      }}
    >
      <Typography variant='labelSmall'>{eventType}</Typography>
    </Box>
  );
};

type SummaryCardProps = {
  testId: string;
  title: string;
  card: BatchRecapSummaryCard;
  isNegative?: boolean;
};

const SummaryCard: FC<SummaryCardProps> = ({
  testId,
  title,
  card,
  isNegative,
}) => {
  const theme = useTheme();
  const valueColor =
    isNegative && card.value > 0 ? theme.color.negative : theme.color.onSurface;
  return (
    // `display: flex` + the descendant overrides make the inner grey card fill
    // the stretched row height, so every summary bubble is the same size
    // regardless of how many lines its description wraps to.
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        '& > *': { width: '100%' },
        '& .MuiCard-root': { height: '100%' },
        // Trim the shared DashboardCard padding/row-gap so the summary bubbles
        // are a bit shorter without touching DashboardCard's defaults.
        '& .MuiCardContent-root': { p: 1.5, '&:last-child': { pb: 1.5 } },
        '& .MuiCardContent-root > .MuiBox-root': { gap: 0.25 },
      }}
      data-testid={testId}
    >
      <DashboardCard
        title={title}
        value={card.value.toLocaleString()}
        description={card.description}
        colors={{
          background: theme.color.surfaceContainerVariant ?? '',
          title: theme.color.onSurfaceVariant ?? '',
          value: valueColor ?? '',
          description: theme.color.onSurfaceVariant ?? '',
        }}
      />
    </Box>
  );
};

type FilterChipProps = {
  filter: BatchRecapFilter;
  label: string;
  count: number;
  selected: boolean;
  onClick?: (filter: BatchRecapFilter) => void;
};

const FilterChip: FC<FilterChipProps> = ({
  filter,
  label,
  count,
  selected,
  onClick,
}) => {
  const theme = useTheme();
  const clickable = onClick !== undefined;
  return (
    <Box
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-pressed={clickable ? selected : undefined}
      onClick={clickable ? () => onClick(filter) : undefined}
      onKeyDown={
        clickable
          ? (event: React.KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick(filter);
              }
            }
          : undefined
      }
      data-testid={`batch-recap-filter-${filter}`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.5,
        py: 0.75,
        borderRadius: 999,
        border: `1px solid ${selected ? theme.color.onSurface : theme.color.outlineVariant}`,
        backgroundColor: selected
          ? theme.color.surfaceContainer
          : theme.color.surface,
        color: theme.color.onSurface,
        cursor: clickable ? 'pointer' : 'default',
        userSelect: 'none',
      }}
    >
      <Typography
        variant='labelSmall'
        sx={{ fontWeight: selected ? 600 : undefined, color: 'inherit' }}
      >
        {`${label} (${count.toLocaleString()})`}
      </Typography>
    </Box>
  );
};

type FilterChipsProps = {
  counts: BatchRecapFilterCounts;
  selected: BatchRecapFilter;
  onFilterChange?: (filter: BatchRecapFilter) => void;
};

const FilterChips: FC<FilterChipsProps> = ({
  counts,
  selected,
  onFilterChange,
}) => {
  const { t } = useTranslation(['batchRecap']);
  const filterLabels: Record<BatchRecapFilter, string> = {
    ALL: t('filters.all'),
    COMPLETED: t('filters.completed'),
    TRADES_SENT: t('filters.tradesSent'),
    FAILED_CANCELED: t('filters.failedOrCanceled'),
  };

  return (
    <Box
      data-testid='batch-recap-filters'
      sx={{
        display: 'flex',
        gap: 1,
        flexWrap: 'wrap',
        px: 2,
        py: 1.5,
      }}
    >
      {ORDERED_FILTERS.map((filter) => (
        <FilterChip
          key={filter}
          filter={filter}
          label={filterLabels[filter]}
          count={counts[filter]}
          selected={filter === selected}
          onClick={onFilterChange}
        />
      ))}
    </Box>
  );
};

const RecapFlag: FC<{ flag: BatchRecapFlag }> = ({ flag }) => {
  const severity =
    flag.variant === 'error'
      ? 'error'
      : flag.variant === 'info'
        ? 'info'
        : 'warning';
  return (
    <Box sx={{ px: 2, pb: 2 }} data-testid='batch-recap-flag'>
      <Alert
        severity={severity}
        variant='outlined'
        sx={{
          alignItems: 'center',
          '& .MuiAlert-action': {
            alignItems: 'center',
            paddingTop: 0,
            marginTop: 0,
            alignSelf: 'center',
          },
          '& .MuiAlert-message': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
        action={
          flag.actionLabel && flag.onAction ? (
            <Typography
              role='button'
              tabIndex={0}
              variant='labelLarge'
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={flag.onAction}
              onKeyDown={(event: React.KeyboardEvent<HTMLSpanElement>) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  flag.onAction?.();
                }
              }}
              data-testid='batch-recap-flag-action'
            >
              {flag.actionLabel}
            </Typography>
          ) : undefined
        }
      >
        <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
          {flag.message}
        </Typography>
      </Alert>
    </Box>
  );
};

const EventRow: FC<{ event: BatchRecapEvent }> = ({ event }) => {
  const { t } = useTranslation(['batchRecap']);
  const tagProps = statusToTagProps(event.status, t);
  return (
    <TableRow data-testid={`batch-recap-row-${event.id}`}>
      <TableCell isFirst>
        <EventTypePill eventType={event.eventType} />
      </TableCell>
      <TableCell>
        <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
          {event.description}
        </Typography>
      </TableCell>
      <TableCell>
        {event.metrics && event.metrics.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'flex-end',
            }}
          >
            {event.metrics.map((metric) => (
              <Typography
                key={`${metric.label}-${metric.value}`}
                variant='bodySmall'
                color='textSecondary'
              >
                {`${metric.value.toLocaleString()} ${metric.label}`}
              </Typography>
            ))}
          </Box>
        ) : null}
      </TableCell>
      <TableCell>
        <StatusTag
          size='small'
          label={tagProps.label}
          color={tagProps.color}
          icon={tagProps.icon}
        />
      </TableCell>
      <TableCell>
        <Typography variant='bodySmall' color='textSecondary'>
          {event.timeElapsedLabel}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

export const BatchRecap: FC<BatchRecapProps> = ({
  data,
  loading,
  error,
  onViewActivity,
  selectedFilter = 'ALL',
  onFilterChange,
  asOfVariant = 'subtitle',
}) => {
  const theme = useTheme();
  const { t } = useTranslation(['batchRecap']);

  const header = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        p: 2,
      }}
    >
      {asOfVariant === 'inline' ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
          }}
        >
          <Typography variant='headingSmall'>{t('title')}</Typography>
          <Typography variant='bodySmall' color='textSecondary'>
            {data.asOfLabel}
          </Typography>
        </Box>
      ) : (
        <Box>
          <Typography variant='headingSmall'>{t('title')}</Typography>
          <Typography variant='bodySmall' color='textSecondary'>
            {`${t('recapAsOf')} ${data.asOfLabel}`}
          </Typography>
        </Box>
      )}
      {onViewActivity !== undefined && (
        <Typography
          variant='labelLarge'
          sx={{ cursor: 'pointer', color: theme.color.primary }}
          onClick={onViewActivity}
          data-testid='batch-recap-view-activity'
        >
          {t('viewActivity')}
        </Typography>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Card sx={{ overflowX: 'auto' }}>
        {header}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            px: 2,
            pb: 2,
          }}
        >
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} width='100%' height='96px' />
          ))}
        </Box>
        <Table>
          {[0, 1, 2].map((i) => (
            <TableRow key={i}>
              <TableCell isFirst>
                <Skeleton width='100%' />
              </TableCell>
              <TableCell>
                <Skeleton width='100%' />
              </TableCell>
              <TableCell>
                <Skeleton width='100%' />
              </TableCell>
              <TableCell>
                <Skeleton width='100%' />
              </TableCell>
              <TableCell>
                <Skeleton width='100%' />
              </TableCell>
            </TableRow>
          ))}
        </Table>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ overflowX: 'auto' }}>
        {header}
        <Typography
          variant='bodyMedium'
          sx={{ color: theme.color.negative, p: 2, fontWeight: 500 }}
        >
          {`${t('errorPrefix')} ${error instanceof Error ? error.message : String(error)}`}
        </Typography>
      </Card>
    );
  }

  const isEmpty = isSummaryEmpty(data.summary) && data.events.length === 0;

  if (isEmpty) {
    return (
      <Card sx={{ overflowX: 'auto' }}>
        {header}
        {data.flag && <RecapFlag flag={data.flag} />}
        <Typography
          variant='bodyMedium'
          color='textSecondary'
          sx={{ p: 4, textAlign: 'center', fontWeight: 500 }}
          data-testid='batch-recap-empty'
        >
          {t('empty')}
        </Typography>
      </Card>
    );
  }

  const { summary, flag, filterCounts, totalEventsLabel, events } = data;
  const showFilterEmpty =
    events.length === 0 &&
    selectedFilter !== 'ALL' &&
    filterCounts !== undefined;

  return (
    // Flex column so a height-constrained parent keeps the header / summary /
    // filters fixed while only the event-log table scrolls. When the parent
    // does not constrain height, the card grows to fit (no scroll) — preserving
    // the default Storybook look.
    <Card sx={{ overflowX: 'auto', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flexShrink: 0 }}>
        {header}

        {flag && <RecapFlag flag={flag} />}

        <Box
          data-testid='batch-recap-cards'
          sx={{
            display: 'flex',
            gap: 2,
            px: 2,
            pb: 2,
          }}
        >
          <SummaryCard
            testId='batch-recap-card-batchesRun'
            title={t('cards.batchesRun.title')}
            card={summary.batchesRun}
          />
          <SummaryCard
            testId='batch-recap-card-tradesSent'
            title={t('cards.tradesSent.title')}
            card={summary.tradesSent}
          />
          <SummaryCard
            testId='batch-recap-card-portfoliosRebalanced'
            title={t('cards.portfoliosRebalanced.title')}
            card={summary.portfoliosRebalanced}
          />
          <SummaryCard
            testId='batch-recap-card-failedOrCanceled'
            title={t('cards.failedOrCanceled.title')}
            card={summary.failedOrCanceled}
            isNegative
          />
        </Box>

        <Box sx={{ px: 2 }}>
          <Typography variant='labelSmall' color='textSecondary'>
            {t('eventLog.sectionTitle')}
          </Typography>
        </Box>

        {filterCounts && (
          <FilterChips
            counts={filterCounts}
            selected={selectedFilter}
            onFilterChange={onFilterChange}
          />
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeadCell isFirst>
                {t('eventLog.columns.type')}
              </TableHeadCell>
              <TableHeadCell>{t('eventLog.columns.description')}</TableHeadCell>
              <TableHeadCell right>
                {t('eventLog.columns.metrics')}
              </TableHeadCell>
              <TableHeadCell>{t('eventLog.columns.status')}</TableHeadCell>
              <TableHeadCell>{t('eventLog.columns.time')}</TableHeadCell>
            </TableRow>
          </TableHead>
          {showFilterEmpty ? (
            <TableRow data-testid='batch-recap-filter-empty'>
              <TableCell colSpan={5}>
                <Typography
                  variant='bodyMedium'
                  color='textSecondary'
                  sx={{
                    py: 4,
                    textAlign: 'center',
                    display: 'block',
                    fontWeight: 500,
                  }}
                >
                  {t('filterNoResults')}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            events.map((event) => <EventRow key={event.id} event={event} />)
          )}
        </Table>
      </Box>

      {totalEventsLabel && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            flexShrink: 0,
          }}
        >
          <Typography variant='bodySmall' color='textSecondary'>
            {totalEventsLabel}
          </Typography>
          {onViewActivity !== undefined && (
            <Typography
              variant='labelLarge'
              sx={{ cursor: 'pointer', color: theme.color.primary }}
              onClick={onViewActivity}
              data-testid='batch-recap-view-activity-footer'
            >
              {t('viewActivity')}
            </Typography>
          )}
        </Box>
      )}
    </Card>
  );
};
